import type { Override } from "@bbj-types/utils";
import {
  Autocomplete,
  type AutocompleteChangeReason,
  type AutocompleteProps,
  type AutocompleteRenderInputParams,
  Box,
  Chip,
  type ChipProps,
  CircularProgress,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
} from "@mui/material";
import type React from "react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

export interface IAutocompleteChipsProps<OptionType, DisableClearable extends boolean | undefined>
  extends Override<
    Omit<
      AutocompleteProps<OptionType, true, DisableClearable, undefined>,
      "filterOptions" | "noOptionsText" | "freeSolo" | "multiple"
    >,
    { renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode }
  > {
  value: OptionType[];
  placeholder?: string;
  getOptionLabel: (option: OptionType) => string;
  TextFieldProps?: TextFieldProps;
  ChipProps?: Omit<ChipProps, "onDelete" | "label">;
  noOptionsText?: ReactNode | ((inputValue: string) => ReactNode);
}

export function AutocompleteChips<OptionType, DisableClearable extends boolean | undefined = undefined>({
  value,
  placeholder = "",
  loading,
  onChange,
  TextFieldProps,
  renderInput,
  ChipProps,
  noOptionsText,
  onInputChange,
  ...props
}: IAutocompleteChipsProps<OptionType, DisableClearable>) {
  const [inputValue, setInputValue] = useState("");

  const handleChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: OptionType[], reason: AutocompleteChangeReason) => {
      if (value && onChange) {
        onChange(event, value, reason);
      }
    },
    [onChange]
  );

  const handleDelete = useCallback(
    (event: React.SyntheticEvent<Element, Event>, index: number) => {
      const newValue = [...value];
      newValue.splice(index, 1);
      if (onChange) {
        onChange(event, newValue, "removeOption");
      }
    },
    [onChange, value]
  );

  const input = (params: AutocompleteRenderInputParams) => (
    <TextField placeholder={placeholder} type="text" {...params} {...TextFieldProps} />
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const noOption = useMemo(
    () => (
      <Box>
        {loading ? (
          <CircularProgress size={15} />
        ) : noOptionsText ? (
          typeof noOptionsText === "function" ? (
            noOptionsText(inputValue)
          ) : (
            noOptionsText
          )
        ) : (
          <Typography variant="body1" color="textSecondary">
            Pas de résultat
          </Typography>
        )}
      </Box>
    ),
    [loading, inputValue]
  );

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Autocomplete
        value={value}
        multiple
        renderInput={renderInput ?? input}
        onChange={handleChange}
        onInputChange={
          onInputChange
            ? (...args) => {
                setInputValue(args[1]);
                onInputChange(...args);
              }
            : undefined
        }
        filterOptions={(options: OptionType[]) => options}
        renderValue={() => null}
        noOptionsText={noOption}
        {...props}
      />
      {value.length > 0 && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {value.map((value: OptionType, index: number) => {
            const label = props.getOptionLabel(value);
            return (
              <Chip
                key={label}
                size="small"
                label={label}
                color="primary"
                onDelete={(event) => handleDelete(event, index)}
                {...ChipProps}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
